import { memo, useId, useSyncExternalStore } from 'react'
import { MASCOT_COLORS, type MascotConfig } from '../mascot-catalog'
import { mascotFurPalette } from '../mascot-colors'
import { MascotHeadwear } from '../MascotDecorations'
import { MascotAccessory } from '../MascotDetails'
import { frisbeePetPose, type LegPose } from './frisbee-pet-motion'
import { FrisbeeDisc } from './FrisbeeDisc'
import { SheepRunner } from './SheepRunner'

const preference = () => window.matchMedia?.('(prefers-reduced-motion: reduce)')
const reducedSnapshot = () => preference()?.matches ?? false
const serverSnapshot = () => false
function subscribeMotion(callback: () => void) {
  const media = preference()
  media?.addEventListener('change', callback)
  return () => media?.removeEventListener('change', callback)
}

function Leg({
  pose,
  root,
  fur,
  paw,
  outline,
}: {
  pose: LegPose
  root: readonly [number, number]
  fur: string
  paw: string
  outline: string
}) {
  const [x, y] = root
  const [kx, ky] = pose.knee
  const [px, py] = pose.paw
  return (
    <g strokeLinecap="round" strokeLinejoin="round">
      <path
        d={`M${x} ${y} Q${x + (kx - x) * 0.3} ${ky - 3} ${kx} ${ky} L${px} ${py - 3}`}
        fill="none"
        stroke={outline}
        strokeWidth="9"
      />
      <path
        d={`M${x} ${y} Q${x + (kx - x) * 0.3} ${ky - 3} ${kx} ${ky} L${px} ${py - 3}`}
        fill="none"
        stroke={fur}
        strokeWidth="7"
      />
      <ellipse
        cx={px + 2}
        cy={py - 1.5}
        rx="5.8"
        ry="3.2"
        fill={paw}
        stroke={outline}
        strokeWidth=".65"
      />
      <path
        d={`M${px + 4} ${py - 2}v1.5m-2-1.7v1.8`}
        stroke={outline}
        strokeWidth=".65"
        opacity=".5"
      />
    </g>
  )
}

/** Side-view runner. Every joint is sampled from the game clock, including pauses. */
export const FrisbeePet = memo(function FrisbeePet({
  config,
  at,
  jumpAt,
  carryingFrisbee = false,
}: {
  config: MascotConfig
  at: number
  jumpAt: number
  carryingFrisbee?: boolean
}) {
  const id = `runner-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const p = mascotFurPalette(config.furColor ?? MASCOT_COLORS[config.color])
  const paint = (name: string) => `url(#${id}-${name})`
  const reduced = useSyncExternalStore(subscribeMotion, reducedSnapshot, serverSnapshot)
  const pose = frisbeePetPose(at, jumpAt, reduced)
  if (config.species === 'sheep')
    return (
      <SheepRunner
        config={config}
        pose={pose}
        at={at}
        carryingFrisbee={carryingFrisbee}
      />
    )
  const jaguar = config.species === 'jaguar'
  const cat = config.species === 'cat' || jaguar
  const body =
    'M-34-30C-37-42-25-47-12-45L6-43Q16-51 24-41L26-29Q20-19 6-20L-9-22Q-29-15-34-30Z'
  const head = jaguar
    ? 'M6-50Q2-69 20-71Q41-73 45-57L46-49Q58-47 56-39Q54-30 39-31L21-32Q6-35 6-50Z'
    : cat
      ? 'M10-49Q5-65 17-68L34-65Q43-60 41-49L48-44Q50-37 41-34L23-34Q10-37 10-49Z'
      : 'M8-48Q3-64 18-67Q36-73 42-58L41-48Q51-48 52-42Q54-34 43-32L24-32Q10-35 8-48Z'
  const frontRoot = [16, -28 + pose.bodyY] as const
  const hindRoot = [-25, -29 + pose.bodyY] as const
  const legPaint = { fur: paint('fur'), paw: p.cream, outline: p.shade }
  return (
    <g
      data-runner-pet={config.species}
      data-gait={pose.airborne ? 'jump' : at === 0 ? 'idle' : 'gallop'}
    >
      <defs>
        <linearGradient id={`${id}-fur`} x1="0" y1="0" x2=".3" y2="1">
          <stop stopColor={p.light} />
          <stop offset=".5" stopColor={p.mid} />
          <stop offset="1" stopColor={p.ear} />
        </linearGradient>
        <linearGradient id={`${id}-ear`} x1="1" y1="0" x2="0" y2="1">
          <stop stopColor={p.mid} />
          <stop offset=".6" stopColor={p.ear} />
          <stop offset="1" stopColor={p.shade} />
        </linearGradient>
        <linearGradient id={`${id}-muzzle`} x2="0" y2="1">
          <stop stopColor="#fff8e9" />
          <stop offset="1" stopColor={p.cream} />
        </linearGradient>
        <clipPath id={`${id}-body`}>
          <path d={body} />
        </clipPath>
        <clipPath id={`${id}-head`}>
          <path d={head} />
        </clipPath>
      </defs>
      <Leg
        pose={pose.farHind}
        root={[-28, -30 + pose.bodyY]}
        fur={p.ear}
        paw={p.mid}
        outline={p.shade}
      />
      <Leg
        pose={pose.farFront}
        root={[10, -29 + pose.bodyY]}
        fur={p.ear}
        paw={p.mid}
        outline={p.shade}
      />
      <g transform={`translate(0 ${pose.bodyY}) rotate(${pose.angle} -5 -33)`}>
        <g transform={`rotate(${pose.tail} -31 -36)`}>
          <path
            d={
              cat
                ? 'M-30-34C-48-34-60-48-54-59Q-49-67-46-60Q-42-50-39-51'
                : 'M-29-35Q-43-34-50-47Q-53-54-49-57Q-45-60-43-52Q-39-43-29-44'
            }
            fill={paint('fur')}
            stroke={p.shade}
            strokeWidth="1.1"
            strokeLinecap="round"
          />
          <path
            d={cat ? 'M-53-60q4-5 6 1' : 'M-50-49q-3-8 1-8'}
            fill="none"
            stroke={jaguar ? '#352b23' : p.cream}
            strokeWidth="3"
            strokeLinecap="round"
          />
          {jaguar && (
            <path
              d="m-43-39-2 4m-5-11-3 3m1-12-4 1"
              stroke="#352b23"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          )}
        </g>
        <g
          transform={`translate(-6 -32) scale(${pose.stretch} ${1 / pose.stretch}) translate(6 32)`}
        >
          <path d={body} fill={paint('fur')} stroke={p.shade} strokeWidth="1.1" />
          <g clipPath={paint('body')}>
            {jaguar && (
              <RunnerRosettes
                points={[
                  [-28, -39],
                  [-16, -41],
                  [-4, -38],
                  [10, -39],
                  [-23, -28],
                  [-10, -29],
                  [4, -28],
                  [19, -29],
                ]}
              />
            )}
            <path d="M-33-22Q-18-33-8-28T23-39L27-14H-35Z" fill={p.cream} opacity=".78" />
            {config.pattern === 'patches' && (
              <path
                d="M-29-47q25-7 24 8T-20-27q-17-1-9-20"
                fill={p.pattern}
                opacity=".65"
              />
            )}
            {config.pattern === 'stripes' && (
              <path
                d="m-24-46 4 10m6-11 4 10m7-9 3 9"
                stroke={p.pattern}
                strokeWidth="3.5"
                strokeLinecap="round"
                opacity=".6"
              />
            )}
            {config.pattern === 'spots' && (
              <g fill={p.pattern} opacity=".65">
                <ellipse cx="-22" cy="-37" rx="5" ry="4" />
                <ellipse cx="-8" cy="-39" rx="3" ry="4" />
                <circle cx="2" cy="-32" r="3" />
              </g>
            )}
            {config.pattern === 'tuxedo' && (
              <path d="M12-45 23-46 31-20 6-20Z" fill={p.cream} />
            )}
            {config.pattern === 'freckles' && (
              <path
                d="M-20-36h.1m5 3h.1m7-6h.1"
                stroke={p.pattern}
                strokeWidth="2"
                strokeLinecap="round"
              />
            )}
          </g>
          <path
            d="M-26-42q15-4 25 0"
            fill="none"
            stroke={p.light}
            strokeWidth="2"
            strokeLinecap="round"
            opacity=".75"
          />
          <path
            d="M-25-36q8-2 10 5"
            fill="none"
            stroke={p.shade}
            strokeWidth=".8"
            opacity=".4"
          />
        </g>
      </g>
      <Leg pose={pose.hind} root={hindRoot} {...legPaint} />
      <Leg pose={pose.front} root={frontRoot} {...legPaint} />
      <g
        transform={`translate(0 ${pose.bodyY * 0.7}) rotate(${pose.headAngle + pose.angle * 0.35} 18 -36)`}
      >
        <g transform="translate(17 -34) rotate(-25) scale(.27) translate(-120 -166)">
          <MascotAccessory
            accessory={config.accessory}
            accessoryColor={config.accessoryColor}
          />
        </g>
        {jaguar ? (
          <ellipse cx="31" cy="-70" rx="7" ry="9" fill="#352b23" stroke={p.shade} />
        ) : (
          cat && (
            <path d="m23-62 7-18 9 18" fill={p.ear} stroke={p.shade} strokeWidth="1" />
          )
        )}
        <path
          d={head}
          fill={paint('fur')}
          stroke={p.shade}
          strokeWidth="1.05"
          strokeLinejoin="round"
        />
        <g clipPath={paint('head')}>
          {jaguar && (
            <RunnerRosettes
              points={[
                [17, -62],
                [29, -65],
                [12, -50],
                [25, -43],
              ]}
            />
          )}
          {config.pattern === 'patches' && (
            <ellipse cx="19" cy="-58" rx="13" ry="11" fill={p.pattern} opacity=".6" />
          )}
          {config.pattern === 'blaze' && (
            <path d="m29-71-2 20 9 10-2-30" fill={p.cream} />
          )}
          {config.pattern === 'tuxedo' && (
            <path d="m35-67-7 23-10 12h38v-18Z" fill={p.cream} />
          )}
          {config.pattern === 'stripes' && (
            <path
              d="m20-70 2 9m6-9 2 8"
              stroke={p.pattern}
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          )}
        </g>
        <path
          d="M31-44Q37-49 46-46Q54-45 51-38Q43-29 31-36Q28-39 31-44"
          fill={paint('muzzle')}
        />
        <path d="M36-35q6 3 12-2-1 8-8 8-5-1-4-6" fill="#654443" />
        {!cat && !carryingFrisbee && (
          <path
            d={`M40-31q5-1 5 2l${pose.tongue * 0.4} 3q-1 5-5 3-3-2 0-8`}
            fill="#e89292"
            stroke="#b96470"
            strokeWidth=".65"
          />
        )}
        <path d="M45-46q7-3 8 1 0 5-6 5Z" fill="#33373b" />
        <ellipse cx="49" cy="-44.5" rx="1.8" ry=".8" fill="#8a9598" />
        <ellipse cx="34" cy="-52.5" rx="4.4" ry="5.8" fill="#fff9ef" />
        <ellipse
          cx="35.5"
          cy="-52"
          rx="3.1"
          ry="4.3"
          fill={config.eyeColor ?? (jaguar ? '#be9b39' : '#815b43')}
        />
        <ellipse cx="36.3" cy="-52" rx="1.65" ry="3" fill="#292c30" />
        <circle cx="36.8" cy="-54" r="1.15" fill="white" />
        <path
          d="m30-61q4-2 7 0"
          fill="none"
          stroke={p.shade}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <ellipse cx="30" cy="-43" rx="3.8" ry="2" fill="#e99e86" opacity=".35" />
        {config.pattern === 'freckles' && (
          <path
            d="M34-41h.1m3 2h.1m2-4h.1"
            stroke={p.pattern}
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        )}
        {jaguar ? (
          <g transform={`rotate(${pose.ear * 0.45} 13 -65)`} data-runner-jaguar-ear>
            <ellipse
              cx="12"
              cy="-68"
              rx="8"
              ry="10"
              fill="#352b23"
              stroke={p.shade}
              strokeWidth="1"
            />
            <ellipse cx="13" cy="-68" rx="5.5" ry="7" fill={paint('fur')} />
            <ellipse cx="14" cy="-67" rx="3" ry="4.5" fill="#d6a482" />
            <path
              d="M7-73q-3 4-1 7"
              stroke="#fff1ce"
              strokeWidth="1.6"
              fill="none"
              strokeLinecap="round"
            />
            <path d="m42-39 14-2m-15 5 13 2" stroke={p.shade} strokeWidth=".8" />
          </g>
        ) : cat ? (
          <>
            <path
              d="M10-55 8-78Q17-76 23-64"
              fill={paint('fur')}
              stroke={p.shade}
              strokeWidth="1"
            />
            <path d="m12-71 1 12 6-6" fill="#dba59e" />
            <path d="m41-39 15-2m-16 5 14 2" stroke={p.shade} strokeWidth=".8" />
          </>
        ) : (
          <g transform={`rotate(${pose.ear} 14 -60)`}>
            <path
              d="M19-64Q6-70-1-59Q-9-49-5-36Q-2-29 4-34Q14-42 19-64Z"
              fill={paint('ear')}
              stroke={p.shade}
              strokeWidth="1"
            />
            <path
              d="M10-61Q0-57-1-44"
              fill="none"
              stroke={p.light}
              strokeWidth="2.5"
              strokeLinecap="round"
              opacity=".6"
            />
          </g>
        )}
        <g transform="translate(24 -65) scale(.24) translate(-120 -50)">
          <MascotHeadwear config={config} />
        </g>
        <path
          d="m24-64 4-1"
          stroke={p.cream}
          strokeWidth="1.7"
          strokeLinecap="round"
          opacity=".7"
        />
        {carryingFrisbee && (
          <g transform="translate(62 -33) rotate(-9)" data-carrying-frisbee>
            <FrisbeeDisc />
            <path
              d="M-23-2q4 5 9 0"
              fill="none"
              stroke={p.shade}
              strokeWidth="2"
              strokeLinecap="round"
            />
          </g>
        )}
      </g>
    </g>
  )
})

function RunnerRosettes({ points }: { points: number[][] }) {
  return (
    <g
      data-runner-rosettes
      fill="#352b23"
      stroke="#352b23"
      strokeWidth="1.3"
      strokeLinecap="round"
    >
      {points.map(([x, y], i) => (
        <g key={i} transform={`translate(${x} ${y}) rotate(${i * 21})`}>
          <path d="M-2-3q-4 0-3 4m1 2q3 3 5 0m2-2q2-4-2-5" fill="none" />
          <circle r=".8" stroke="none" />
        </g>
      ))}
    </g>
  )
}
